import process from 'node:process';
import { createClient } from '@chatsdet/sdk';
import {
  createUserEventRecorder,
  type CliCommandBaseMetadata,
  type UserEventRecorder,
} from '@chatsdet/user-events';
import { resolveOptionalConfig } from './auth.js';
import { getCliDiagnosticMetadata } from './cli-diagnostics.js';
import { debug } from './debug.js';

const SUBCOMMAND_COMMANDS = new Set([
  'api-catalog',
  'attachment',
  'attachments',
  'auth',
  'credential',
  'credential-template',
  'credential-templates',
  'docs',
  'feature-flag',
  'issues',
  'knobs',
  'local',
  'property',
  'release',
  'sandbox',
  'session',
  'tag',
  'tags',
  'task',
  'test-suite',
  'workflow',
]);

const UNTRACKED_COMMANDS = new Set(['help', 'upgrade', 'version']);

export class ProcessExitSignal extends Error {
  readonly exitCode: number;

  constructor(exitCode: number) {
    super(`Process exited with code ${exitCode}`);
    this.name = 'ProcessExitSignal';
    this.exitCode = exitCode;
  }
}

function shouldTrack(argv: string[]): boolean {
  const command = argv[0];
  if (!command || UNTRACKED_COMMANDS.has(command)) return false;
  if (argv.includes('--help') || argv.includes('-h')) return false;
  return true;
}

function getSubcommand(argv: string[]): string | undefined {
  const command = argv[0];
  if (!command || !SUBCOMMAND_COMMANDS.has(command)) return undefined;
  const candidate = argv[1];
  if (!candidate || candidate.startsWith('-')) return undefined;
  return candidate;
}

function getApiHost(apiUrl: string): string | undefined {
  try {
    return new URL(apiUrl).host;
  } catch {
    return undefined;
  }
}

function getArgNames(argv: string[]): string[] {
  return argv.filter((arg) => arg.startsWith('-'));
}

function looksLikeWorkflowRunId(value: string): boolean {
  return value.length >= 8 && !/^[a-z-]+$/i.test(value.slice(0, 8));
}

function getWorkflowDebugMetadata(argv: string[]): {
  workflowDebugSubcommand?: string;
  workflowDebugTarget?: string;
  workflowDebugTargetSource?: string;
} {
  if (argv[0] !== 'workflow' || argv[1] !== 'debug') return {};
  const [maybeSubcommand, ...rest] = argv.slice(2);
  if (!maybeSubcommand || maybeSubcommand === 'help' || maybeSubcommand.startsWith('-')) {
    return {};
  }
  if (looksLikeWorkflowRunId(maybeSubcommand)) {
    return {
      workflowDebugSubcommand: 'overview',
      workflowDebugTarget: maybeSubcommand,
      workflowDebugTargetSource: 'bare-run',
    };
  }

  const target = rest.find((arg) => !arg.startsWith('-'));
  return {
    workflowDebugSubcommand: maybeSubcommand,
    workflowDebugTarget: target,
    workflowDebugTargetSource: target ? 'subcommand-run' : undefined,
  };
}

function buildBaseMetadata(
  argv: string[],
  cliVersion: string,
  apiUrl: string
): CliCommandBaseMetadata {
  const topLevelCommand = argv[0] ?? 'unknown';
  const subcommand = getSubcommand(argv);
  return {
    commandPath: subcommand ? `${topLevelCommand} ${subcommand}` : topLevelCommand,
    topLevelCommand,
    subcommand,
    cliVersion,
    apiHost: getApiHost(apiUrl),
    isInteractive: Boolean(process.stdin.isTTY && process.stdout.isTTY),
    args: getArgNames(argv),
    ...getWorkflowDebugMetadata(argv),
  };
}

function errorMetadata(error: unknown): {
  errorKind: string;
  errorName?: string;
  errorMessage: string;
} & Record<string, string | number | boolean | null | undefined> {
  const cause = error instanceof Error && error.cause instanceof Error ? error.cause : null;
  const causeCode = cause && (cause as Error & { code?: unknown }).code;
  const errorName = error instanceof Error ? error.name : undefined;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const terminationKind =
    errorName === 'TimeoutError' || /timed out|aborted due to timeout/i.test(errorMessage)
      ? 'timeout'
      : errorName === 'TypeError' && errorMessage.trim().toLowerCase() === 'terminated'
        ? 'terminated'
        : null;
  return {
    errorKind: error instanceof Error ? error.name : typeof error,
    errorName,
    errorMessage,
    cliErrorCauseName: cause?.name ?? null,
    cliErrorCauseMessage: cause?.message ?? null,
    cliErrorCauseCode: typeof causeCode === 'string' ? causeCode : null,
    cliTerminationKind: terminationKind,
    cliTerminationExpected: terminationKind ? false : null,
    ...getCliDiagnosticMetadata(error),
  };
}

const PLAYWRIGHT_LOCAL_SETUP_ERROR_PATTERNS = [
  /browserType\.launch: Executable doesn't exist at .*ms-playwright/i,
  /Looks like Playwright was just installed or updated\.[\s\S]*playwright install/i,
  /Could not load Playwright trace viewer\. Make sure playwright is installed\./i,
];

const WORKFLOW_CACHE_EXPECTED_INPUT_ERROR_PATTERNS = [
  /^No (?:before|input|after)-snapshot HTML reconstructable for trace action \d+ — the action may predate page load or the trace omitted its snapshot$/,
  /^Invalid JSON in --recipe file: /,
  /^Invalid --recipe file: expected a JSON object$/,
  /^Could not read --recipe file: /,
  /^Trace file not found: /,
];

export function isExpectedCliInputError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    /^Action index \d+ out of range \(0-\d+\)$/.test(error.message) ||
    WORKFLOW_CACHE_EXPECTED_INPUT_ERROR_PATTERNS.some((pattern) => pattern.test(error.message)) ||
    PLAYWRIGHT_LOCAL_SETUP_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
  );
}

export async function createCliUserEventRecorder(
  argv: string[],
  cliVersion: string
): Promise<{ recorder: UserEventRecorder; metadata: CliCommandBaseMetadata } | null> {
  if (!shouldTrack(argv)) return null;

  // Not every command uses resolveConfig() yet. Until CLI config resolution is
  // centralized, tracking can target a different backend than the command body.
  const config = await resolveOptionalConfig(argv);
  if (!config) return null;

  const client = createClient({
    baseUrl: config.apiUrl,
    token: config.token,
  });

  return {
    recorder: createUserEventRecorder({
      client,
      origin: 'cli',
      onError: (error: unknown) => {
        // Tracking is best-effort diagnostics and must never affect command
        // execution or pollute output (e.g. wrap `--json` payloads). Stay silent
        // unless the user opted into debug logging.
        const message = error instanceof Error ? error.message : String(error);
        debug(`user event tracking skipped: ${message}`);
      },
    }),
    metadata: buildBaseMetadata(argv, cliVersion, config.apiUrl),
  };
}

export async function recordCliInstrumentationFailure(
  argv: string[],
  cliVersion: string,
  stage: 'recorder-init' | 'record-started' | 'record-completed' | 'record-failed',
  error: unknown
): Promise<void> {
  if (!shouldTrack(argv)) return;

  try {
    const config = await resolveOptionalConfig(argv);
    if (!config) return;

    const client = createClient({
      baseUrl: config.apiUrl,
      token: config.token,
    });
    const recorder = createUserEventRecorder({
      client,
      origin: 'cli',
    });
    await recorder.recordCommandInstrumentationFailed({
      ...buildBaseMetadata(argv, cliVersion, config.apiUrl),
      stage,
      ...errorMetadata(error),
      reportToSentry: true,
      sentryFingerprint: ['canary-cli', 'instrumentation-failed', stage],
    });
  } catch {
    // Diagnostics must never affect command execution.
  }
}

export const __internals = {
  errorMetadata,
  getArgNames,
  getSubcommand,
  getWorkflowDebugMetadata,
  isExpectedCliInputError,
  shouldTrack,
};
